/**
 * The plain-words list of what the customer Settings page's one Save is about
 * to change, shown in its one reason dialog (docs/features/credit-control/
 * 06_ui_strategy.md §2). Pure: it compares what is saved with what has been
 * edited and words the difference with the labels the backend sent (the
 * method, terms-basis and PO rule options).
 *
 * Each owner's settings are described by its own function, as each is written
 * by its own call: credit control's payment settings, and the sales ledger's
 * invoicing settings (worded with the labels and options its read sent).
 */
import type {
  CreditOption,
  PaymentMethod,
  PaymentSettingsValues,
  PaymentTermsValues,
  PoRule,
  TermsBasis,
} from "../data/credit-control-types";
import type {
  InvoicingSettingsValues,
  InvoicingSettingsView,
} from "../data/sales-ledger-types";
import { creditLimitPounds } from "./credit-control-presentation";
import { formatCurrencyGbp } from "./formatters";

export interface PaymentChangeLabels {
  methods: CreditOption<PaymentMethod>[];
  termsBasis: CreditOption<TermsBasis>[];
  poRule: CreditOption<PoRule>[];
}

const labelOf = <T extends string>(
  options: CreditOption<T>[],
  value: T,
): string => options.find((o) => o.value === value)?.label ?? value;

/** A cleared PO format, worded as the backend's change log words it. */
const NO_PO_FORMAT = "(none)";
const NO_DAY_NAMED = "(none)";

const limitText = (pence: number | null): string => {
  const pounds = creditLimitPounds(pence);
  return pounds === null ? "None" : formatCurrencyGbp(pounds);
};

function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  return `${n}${{ 1: "st", 2: "nd", 3: "rd" }[n % 10] ?? "th"}`;
}

/**
 * "30 days from end of invoice month", "20th of the following month": the
 * number with the basis the backend worded. A day-of basis names a day of the
 * month, so its number is an ordinal.
 */
function termsText(terms: PaymentTermsValues, labels: PaymentChangeLabels) {
  const basis = labelOf(labels.termsBasis, terms.termsBasis);
  return terms.termsBasis.startsWith("day_of_")
    ? `${ordinal(terms.termsDays)} ${basis}`
    : `${terms.termsDays} days ${basis}`;
}

/** Methods added or removed, then each credit-account term that changed (all of them when credit account is new). */
export function describePaymentChanges(
  saved: PaymentSettingsValues,
  edited: PaymentSettingsValues,
  labels: PaymentChangeLabels,
): string[] {
  const lines: string[] = [];

  const added = edited.paymentMethods.filter(
    (m) => !saved.paymentMethods.includes(m),
  );
  const removed = saved.paymentMethods.filter(
    (m) => !edited.paymentMethods.includes(m),
  );
  if (added.length > 0 || removed.length > 0) {
    const parts = [
      ...added.map((m) => `+ ${labelOf(labels.methods, m)}`),
      ...removed.map((m) => `− ${labelOf(labels.methods, m)}`),
    ];
    lines.push(`Payment methods: ${parts.join(", ")}`);
  }

  const before = saved.terms;
  const after = edited.terms;
  if (!after) return lines;
  if (!before) {
    lines.push(
      `Payment terms: ${termsText(after, labels)}`,
      `PO rule: ${labelOf(labels.poRule, after.poRule)}`,
    );
    if (after.poFormat) lines.push(`PO format: ${after.poFormat.trim()}`);
    lines.push(`Credit limit: ${limitText(after.creditLimitPence)}`);
    return lines;
  }
  if (
    before.termsDays !== after.termsDays ||
    before.termsBasis !== after.termsBasis
  ) {
    lines.push(
      `Payment terms: ${termsText(before, labels)} → ${termsText(after, labels)}`,
    );
  }
  if (before.poRule !== after.poRule) {
    lines.push(
      `PO rule: ${labelOf(labels.poRule, before.poRule)} → ${labelOf(labels.poRule, after.poRule)}`,
    );
  }
  const formatBefore = before.poFormat?.trim() || null;
  const formatAfter = after.poFormat?.trim() || null;
  if (formatBefore !== formatAfter) {
    lines.push(
      `PO format: ${formatBefore ?? NO_PO_FORMAT} → ${formatAfter ?? NO_PO_FORMAT}`,
    );
  }
  if (before.creditLimitPence !== after.creditLimitPence) {
    lines.push(
      `Credit limit: ${limitText(before.creditLimitPence)} → ${limitText(after.creditLimitPence)}`,
    );
  }
  return lines;
}

/**
 * Each invoicing setting being changed, "Made-to-order work is invoiced: On
 * despatch → On delivery", in the order the page shows them, with the labels
 * and options the read sent. `changes` holds only the settings that differ.
 */
export function describeInvoicingChanges(
  invoicing: InvoicingSettingsView,
  changes: Partial<InvoicingSettingsValues>,
): string[] {
  const lines: string[] = [];
  const choices = [
    ["madeToOrderInvoiceTrigger", invoicing.madeToOrderInvoiceTrigger],
    ["invoiceGrouping", invoicing.invoiceGrouping],
    ["invoiceCutOff", invoicing.invoiceCutOff],
    ["invoiceCutOffDay", invoicing.invoiceCutOffDay],
    ["postageInvoicing", invoicing.postageInvoicing],
    ["stockBilling", invoicing.stockBilling],
  ] as const;
  for (const [key, setting] of choices) {
    const after = changes[key];
    if (after === undefined) continue;
    const options = setting.options as CreditOption<string>[];
    // A cut-off with no day named reads "the default": the backend gives it Friday or the last day of the month.
    const before = labelOf(options, setting.value) || NO_DAY_NAMED;
    lines.push(`${setting.label}: ${before} → ${labelOf(options, after)}`);
  }
  const carrier = changes.postsOnOwnCarrierAccount;
  if (carrier !== undefined) {
    const yesNo = (on: boolean) => (on ? "Yes" : "No");
    lines.push(
      `${invoicing.postsOnOwnCarrierAccount.label}: ${yesNo(invoicing.postsOnOwnCarrierAccount.value)} → ${yesNo(carrier)}`,
    );
  }
  return lines;
}
